import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchVideoComments } from '../services/youtubeService';
import { analyzeCommentsWithLearning } from '../services/learningSystem';
import { loadAnalysis, saveAnalysis } from '../services/analysisStorage';
import { useAuth } from '../contexts/AuthContext';
import { validateCachedData } from '../utils/validateAnalysisData';
import CommentCard from '../components/CommentCard';
import VideoInfo from '../components/VideoInfo';
import CommentsResume from '../components/CommentsResume';
import ReplyForm from '../components/ReplyForm';
import { AlertTriangle, ArrowLeft } from 'react-feather';

const VideoAnalysisPage = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [videoMetadata, setVideoMetadata] = useState(null);
  const [analysisDate, setAnalysisDate] = useState(null);
  const [activeTab, setActiveTab] = useState('buyers');
  const [insightTab, setInsightTab] = useState('revenue-signals'); // For Instant Audience Insight tabs
  const [comments, setComments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [replyingTo, setReplyingTo] = useState(null);
  const commentsPerPage = 10;

  useEffect(() => {
    if (videoId && currentUser?.uid) {
      loadVideoAnalysis();
    } else if (!currentUser?.uid) {
      setError('Please sign in to view video analysis');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, currentUser?.uid]);

  const loadVideoAnalysis = async () => {
    if (!videoId || !currentUser?.uid) return;

    setLoading(true);
    setError(null);

    try {
      // Try to load from cache first
      const cached = await loadAnalysis(currentUser.uid, videoId);
      
      if (cached && cached.analysis && cached.videoMetadata) {
        // Validate cached data before using it
        const validated = validateCachedData(cached);
        if (validated) {
          console.log('Loading analysis from cache');
          setAnalysis(validated.analysis);
          setVideoMetadata(validated.videoMetadata);
          setComments(validated.comments || []);
          setAnalysisDate(validated.analyzedAt ? new Date(validated.analyzedAt) : new Date());
          setActiveTab('buyers');
          setLoading(false);
          return;
        } else {
          console.warn('Cached data failed validation, proceeding with fresh analysis');
        }
      }

      // If no cache, fetch and analyze
      const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
      if (!apiKey) {
        setError('YouTube API key is not configured');
        setLoading(false);
        return;
      }

      const result = await fetchVideoComments(videoId, apiKey, 100);
      const { comments: fetchedComments, videoMetadata: metadata } = result;
      
      if (fetchedComments.length === 0) {
        setError('No comments found for this video');
        setLoading(false);
        return;
      }

      // Analyze comments
      const channelId = metadata.channelId || videoId;
      let analyzed;
      try {
        analyzed = await analyzeCommentsWithLearning(
          fetchedComments,
          videoId,
          channelId,
          metadata.commentCount
        );
        
        if (!analyzed) {
          throw new Error('Analysis returned no results');
        }
      } catch (analysisError) {
        console.error('Error during analysis:', analysisError);
        throw new Error(`Analysis failed: ${analysisError.message || 'Unknown error'}`);
      }

      // Validate analysis before setting state
      const { validateAnalysis } = await import('../utils/validateAnalysisData');
      const validatedAnalysis = validateAnalysis(analyzed);
      
      if (!validatedAnalysis) {
        throw new Error('Analysis validation failed - received invalid analysis data');
      }

      setAnalysis(validatedAnalysis);
      setVideoMetadata(metadata);
      setComments(fetchedComments);
      setAnalysisDate(new Date());
      setActiveTab('buyers');

      // Save analysis to cache (use validated analysis)
      if (currentUser?.uid) {
        try {
          await saveAnalysis(currentUser.uid, videoId, validatedAnalysis, metadata, fetchedComments);
          console.log('Analysis cached successfully');
        } catch (cacheError) {
          console.error('Failed to cache analysis:', cacheError);
        }
      }
    } catch (err) {
      console.error('Error loading video analysis:', err);
      setError(err.message || 'Failed to load video analysis');
    } finally {
      setLoading(false);
    }
  };

  const getCommentsForTab = () => {
    if (!analysis || typeof analysis !== 'object') return [];

    // Ensure all arrays are valid arrays and filter out invalid comments
    const ensureArray = (arr) => {
      if (!Array.isArray(arr)) return [];
      return arr.filter(comment => comment && typeof comment === 'object' && comment.id);
    };

    switch (activeTab) {
      case 'buyers':
        return ensureArray(analysis.potentialBuyers);
      case 'friction':
        return ensureArray(analysis.frictionPoints);
      case 'questions':
        return ensureArray(analysis.questions);
      case 'suggestions':
        return ensureArray(analysis.suggestions);
      case 'positive':
        return ensureArray(analysis.positiveFeedback);
      case 'all':
        return [
          ...ensureArray(analysis.potentialBuyers), 
          ...ensureArray(analysis.frictionPoints), 
          ...ensureArray(analysis.questions),
          ...ensureArray(analysis.suggestions),
          ...ensureArray(analysis.positiveFeedback),
          ...ensureArray(analysis.otherComments)
        ];
      default:
        return [];
    }
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'buyers':
        return `Potential Buyers (${analysis?.buyerCount || 0})`;
      case 'friction':
        return `Friction Points (${analysis?.frictionCount || 0})`;
      case 'questions':
        return `Questions (${analysis?.questionCount || 0})`;
      case 'suggestions':
        return `Suggestions (${analysis?.suggestionCount || 0})`;
      case 'positive':
        return `Positive Feedback (${analysis?.positiveCount || 0})`;
      case 'all':
        return `All Comments (${analysis?.totalComments || 0})`;
      default:
        return 'Comments';
    }
  };

  // Handle tab switching and comment navigation
  useEffect(() => {
    const handleSwitchToAllComments = (e) => {
      setActiveTab('all');
      const commentId = e.detail?.commentId;
      if (commentId && comments.length > 0) {
        const allComments = [
          ...(analysis?.potentialBuyers || []), 
          ...(analysis?.frictionPoints || []), 
          ...(analysis?.questions || []),
          ...(analysis?.suggestions || []),
          ...(analysis?.positiveFeedback || []),
          ...(analysis?.otherComments || [])
        ];
        const commentIndex = allComments.findIndex(c => c.id === commentId);
        if (commentIndex >= 0) {
          const targetPage = Math.floor(commentIndex / commentsPerPage) + 1;
          setCurrentPage(targetPage);
          setTimeout(() => {
            const element = document.getElementById(`comment-${commentId}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
      }
    };

    window.addEventListener('switchToAllComments', handleSwitchToAllComments);
    return () => window.removeEventListener('switchToAllComments', handleSwitchToAllComments);
  }, [comments, commentsPerPage, analysis]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-10 font-sans bg-gradient-to-br from-[#1a1b2e] to-[#2d1b3d] text-white">
        <div className="w-12 h-12 border-4 border-primary-500 border-opacity-20 border-t-primary-500 rounded-full animate-spin mb-4"></div>
        <p className="text-white text-opacity-70 text-base">
          Loading analysis...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 font-sans bg-gradient-to-br from-[#1a1b2e] to-[#2d1b3d] text-white">
        <div className="flex items-center justify-center gap-3 p-5 bg-error-light border border-error-base text-error-dark rounded-xl mb-6 max-w-[600px]">
          <AlertTriangle size={24} />
          <span>{error}</span>
        </div>
        <button
          onClick={() => navigate('/videos')}
          className="flex items-center gap-2 py-3 px-6 bg-primary-500 text-white rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 hover:bg-primary-600 hover:-translate-y-0.5 hover:shadow-lg"
        >
          <ArrowLeft size={18} />
          Back to Videos
        </button>
      </div>
    );
  }

  if (!analysis || !videoMetadata || typeof analysis !== 'object' || typeof videoMetadata !== 'object') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 font-sans bg-gradient-to-br from-[#1a1b2e] to-[#2d1b3d] text-white">
        <p className="text-white text-opacity-70 mb-6">
          No analysis data available for this video.
        </p>
        <button
          onClick={() => navigate('/videos')}
          className="flex items-center gap-2 py-3 px-6 bg-primary-500 text-white rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 hover:bg-primary-600 hover:-translate-y-0.5 hover:shadow-lg"
        >
          <ArrowLeft size={18} />
          Back to Videos
        </button>
      </div>
    );
  }

  const commentsToShow = getCommentsForTab();
  const totalPages = Math.ceil(commentsToShow.length / commentsPerPage);
  const startIndex = (currentPage - 1) * commentsPerPage;
  const endIndex = startIndex + commentsPerPage;
  const paginatedComments = commentsToShow.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen font-sans bg-gradient-to-br from-[#1a1b2e] to-[#2d1b3d] text-white p-4 sm:p-6 md:p-8 overflow-x-hidden">
      <div className="max-w-7xl mx-auto min-w-0 w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate('/videos')}
          className="flex items-center gap-2 mb-4 sm:mb-6 py-2 sm:py-2.5 px-3 sm:px-4 bg-white bg-opacity-5 border border-white border-opacity-10 rounded-lg text-white text-xs sm:text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-white hover:bg-opacity-10 hover:border-primary-500"
        >
          <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
          <span className="hidden sm:inline">Back to Videos</span>
          <span className="sm:hidden">Back</span>
        </button>

        {/* Video Info */}
        <div className="mb-8">
          <VideoInfo videoMetadata={videoMetadata} analysisDate={analysisDate} />
        </div>
        
        {/* Comments Resume/Summary */}
        {(analysis.resume || analysis.commentsResume) && (
          <div className="mb-8">
            <CommentsResume resume={analysis.resume || analysis.commentsResume} />
          </div>
        )}

        {/* Instant Audience Insight Section */}
        <div className="bg-white rounded-2xl shadow-md mb-8 overflow-hidden">
          {/* Header */}
          <div className="p-4 sm:p-6 md:p-8 border-b border-gray-200">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 break-words">Instant Audience Insight</h2>
            <p className="text-sm sm:text-base text-gray-600 break-words">
              Expert analysis based on {analysis.totalComments || videoMetadata.commentCount || 0} comments
            </p>
          </div>

          {/* Insight Tabs */}
          <div className="flex gap-2 px-4 sm:px-6 md:px-8 pt-4 border-b-2 border-gray-200 overflow-x-auto">
            {[
              { id: 'revenue-signals', label: 'Revenue Signals' },
              { id: 'audience-pulse', label: 'Audience Pulse' },
              { id: 'conversion-blockers', label: 'Conversion Blockers' },
              { id: 'unanswered-demand', label: 'Unanswered Demand' },
              { id: 'opportunities', label: 'Opportunities' },
              { id: 'next-actions', label: 'Next Actions' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setInsightTab(tab.id)}
                className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 bg-transparent border-none text-xs sm:text-sm font-semibold cursor-pointer transition-all duration-200 mb-0 border-b-3 border-transparent -mb-0.5 whitespace-nowrap ${
                  insightTab === tab.id 
                    ? 'text-primary-500 border-b-primary-500 bg-gray-50' 
                    : 'text-gray-600 hover:text-primary-500 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6 md:p-8">
            {insightTab === 'revenue-signals' && analysis.revenueSignals && typeof analysis.revenueSignals === 'object' && (
              <div className="space-y-6">
                {/* Estimated Purchase Intent Level */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 break-words">Estimated Purchase Intent Level</h3>
                  <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <span className="text-2xl sm:text-3xl font-bold text-orange-600">
                        {analysis.revenueSignals.buyingReadiness || 'Low'}
                      </span>
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs sm:text-sm font-semibold">
                        {analysis.revenueSignals.buyingReadiness || 'Low'}
                      </span>
                    </div>
                    <p className="text-sm sm:text-base text-gray-700 break-words overflow-wrap-anywhere">
                      {analysis.buyerCount || 0} of {analysis.totalComments || videoMetadata.commentCount || 0} comments ({((analysis.buyerCount || 0) / (analysis.totalComments || videoMetadata.commentCount || 1) * 100).toFixed(1)}%) show purchase-related intent
                    </p>
                  </div>
                </div>

                {/* Strongest Buying Signals */}
                {Array.isArray(analysis.revenueSignals.topBuyingSignals) && analysis.revenueSignals.topBuyingSignals.length > 0 && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 break-words">Strongest Buying Signals</h3>
                    <div className="space-y-2">
                      {analysis.revenueSignals.topBuyingSignals.slice(0, 5).map((signal, index) => {
                        const signalText = signal?.text || signal || '';
                        const signalCount = signal?.count || 1;
                        if (!signalText) return null;
                        return (
                          <div key={index} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                            <span className="text-sm sm:text-base text-gray-800 break-words overflow-wrap-anywhere flex-1 pr-2">
                              "{signalText}"
                            </span>
                            <span className="flex-shrink-0 px-2 py-1 bg-green-200 text-green-800 rounded text-xs sm:text-sm font-semibold">
                              {signalCount}x
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Main Objections Preventing Conversion */}
                {Array.isArray(analysis.revenueSignals.mainObjections) && analysis.revenueSignals.mainObjections.length > 0 && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 break-words">Main Objections Preventing Conversion</h3>
                    <div className="space-y-2">
                      {analysis.revenueSignals.mainObjections.map((objection, index) => {
                        const objectionText = objection?.text || objection || '';
                        const objectionCount = objection?.count || 1;
                        if (!objectionText) return null;
                        return (
                          <div key={index} className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
                            <span className="text-sm sm:text-base text-gray-800 break-words overflow-wrap-anywhere flex-1 pr-2">
                              {objectionText}
                            </span>
                            <span className="flex-shrink-0 px-2 py-1 bg-orange-200 text-orange-800 rounded text-xs sm:text-sm font-semibold">
                              {objectionCount}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Placeholder for other tabs */}
            {insightTab !== 'revenue-signals' && (
              <div className="text-center py-12 text-gray-500">
                <p className="break-words">Content for {insightTab.replace('-', ' ')} coming soon.</p>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-8">
          <div className="bg-gradient-stat-buyers rounded-xl p-4 sm:p-6 text-center transition-transform duration-200 hover:-translate-y-1">
            <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">{analysis.buyerCount || 0}</div>
            <div className="text-xs text-gray-600 font-semibold uppercase tracking-wide">Potential Buyers</div>
          </div>
          <div className="bg-gradient-stat-friction rounded-xl p-4 sm:p-6 text-center transition-transform duration-200 hover:-translate-y-1">
            <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">{analysis.frictionCount || 0}</div>
            <div className="text-xs text-gray-600 font-semibold uppercase tracking-wide">Friction Points</div>
          </div>
          <div className="bg-gradient-stat-total rounded-xl p-4 sm:p-6 text-center transition-transform duration-200 hover:-translate-y-1">
            <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">{analysis.totalComments || 0}</div>
            <div className="text-xs text-gray-600 font-semibold uppercase tracking-wide">Total Comments</div>
          </div>
        </div>

        {/* Tabs and Comments Section */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-md overflow-hidden">
          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b-2 border-gray-200 pb-0 flex-wrap overflow-x-auto">
            {[
              { id: 'buyers', label: 'Potential Buyers', count: analysis.buyerCount || 0 },
              { id: 'friction', label: 'Friction Points', count: analysis.frictionCount || 0 },
              { id: 'questions', label: 'Questions', count: analysis.questionCount || 0 },
              { id: 'suggestions', label: 'Suggestions', count: analysis.suggestionCount || 0 },
              { id: 'positive', label: 'Positive', count: analysis.positiveCount || 0 },
              { id: 'all', label: 'All Comments', count: analysis.totalComments || 0 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setCurrentPage(1); }}
                className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 bg-transparent border-none text-xs sm:text-sm font-semibold cursor-pointer transition-all duration-200 mb-0 border-b-3 border-transparent -mb-0.5 whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'text-primary-500 border-b-primary-500 bg-gray-50' 
                    : 'text-gray-600 hover:text-primary-500 hover:bg-gray-50'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Comments List */}
          {paginatedComments.length === 0 ? (
            <div className="text-center py-12 px-6 text-gray-500 text-base">
              <p>No comments found in this category.</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-5">
                  {getTabTitle()}
                </h2>
                <div className="flex flex-col gap-4">
                  {paginatedComments.map((comment, index) => {
                    // Safety check: ensure comment is valid
                    if (!comment || typeof comment !== 'object' || !comment.id) {
                      console.warn('Invalid comment at index', index, comment);
                      return null;
                    }
                    
                    const displayType = comment.buyerScore >= 0.3 ? 'buyer' : comment.frictionScore >= 0.3 ? 'friction' : 'other';
                    
                    return (
                      <div key={comment.id || `comment-${index}`}>
                        <CommentCard
                          comment={comment}
                          type={displayType}
                          videoMetadata={videoMetadata}
                        />
                        {replyingTo === comment.id ? (
                          <div className="ml-4 mt-3">
                            <ReplyForm
                              commentId={comment.id}
                              onReplyPosted={() => setReplyingTo(null)}
                              onCancel={() => setReplyingTo(null)}
                            />
                          </div>
                        ) : (
                          <button
                            onClick={() => setReplyingTo(comment.id)}
                            className="ml-4 mt-2 px-4 py-2 bg-primary-500 text-white rounded-md text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-primary-600 hover:-translate-y-0.5 hover:shadow-lg"
                          >
                            Reply
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 sm:gap-3 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 flex-wrap">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium cursor-pointer transition-all duration-200 ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                        : 'bg-primary-500 text-white hover:bg-primary-600 hover:-translate-y-0.5 hover:shadow-lg'
                    }`}
                  >
                    Previous
                  </button>
                  <span className="px-3 sm:px-5 py-2 sm:py-2.5 text-gray-700 text-xs sm:text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium cursor-pointer transition-all duration-200 ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                        : 'bg-primary-500 text-white hover:bg-primary-600 hover:-translate-y-0.5 hover:shadow-lg'
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoAnalysisPage;
